'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { Ip, Tp, ReviewedProduct, Fp, ReviewFlag } from './readiness-review';
import {
  ProductionArtifact,
  ProductionIllustrativeBadge,
  ProductionBadge,
} from './production-primitives';
import { ProductionIcon } from './deliverable-previews';
import { J, $d, ef } from './sell-market-animation';
import { w } from './workflow-facts';
import { ShowcaseIcon } from './scene-primitives';
export function CollectionReview({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = Ip.filter((t) => e >= t.at).length;
  return (
    <ProductionArtifact
      icon={'search'}
      title={translateShowcaseNode(showcaseCopy, 'FW27 Collection Review')}
      view={e >= 3500 ? 'Flags' : 'Targets'}
      footer={
        <React.Fragment>
          <span
            className={'text-ink-3 flex min-w-0 items-center gap-2 text-[13px]'}
          >
            <ProductionIcon name={'flag'} className={'size-4 shrink-0'} />
            <span className={'num truncate'}>
              {translateShowcaseNode(
                showcaseCopy,
                t === 0
                  ? 'Checking every product against the plan'
                  : `${t} ${t === 1 ? 'flag' : 'flags'} to resolve before lock`
              )}
            </span>
          </span>
          <ProductionIllustrativeBadge className={'ml-auto'} />
        </React.Fragment>
      }
    >
      <div
        className={
          'grid gap-5 p-4 sm:p-5 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] md:gap-6'
        }
      >
        <div className={'grid grid-cols-3 content-start gap-x-2.5 gap-y-3.5'}>
          {translateShowcaseNode(
            showcaseCopy,
            Tp.map((t, n) => <ReviewedProduct key={t.alt} index={n} t={e} />)
          )}
        </div>
        <div className={'flex min-w-0 flex-col'}>
          <p className={'label'}>
            {translateShowcaseNode(showcaseCopy, 'Against the plan')}
          </p>
          <ul className={'mt-1 flex flex-col divide-y divide-[var(--line)]'}>
            {translateShowcaseNode(
              showcaseCopy,
              Fp.map((t) => <ReviewFlag key={t.label} row={t} t={e} />)
            )}
          </ul>
          <ul className={'mt-3 flex flex-col gap-2'}>
            {translateShowcaseNode(
              showcaseCopy,
              Ip.map((t) => (
                <li
                  key={t.kind}
                  {...J(e, t.at)}
                  className={
                    'pd-pop flex gap-2.5 rounded-[10px] bg-[var(--pd-amber-wash)] p-2.5'
                  }
                >
                  <span className={'mt-0.5 text-[var(--pd-amber)]'}>
                    <ProductionIcon name={t.glyph} className={'size-4'} />
                  </span>
                  <span className={'text-ink text-[13px] leading-[1.45]'}>
                    <span className={'font-medium'}>
                      {translateShowcaseNode(showcaseCopy, t.kind)}
                      {translateShowcaseNode(showcaseCopy, '. ')}
                    </span>
                    <span className={'text-ink-2'}>
                      {translateShowcaseNode(showcaseCopy, t.text)}
                    </span>
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </ProductionArtifact>
  );
}
export const Bp = (e, t = !1) =>
  `€${e.toLocaleString('en-US', {
    minimumFractionDigits: t ? 2 : 0,
    maximumFractionDigits: t ? 2 : 0,
  })}`;
export const Vp = w.cost;
export const Hp = 2600;
export const Up = 260;
export const Wp = Vp[6];
export const Gp = Vp[0];
export const Kp = Hp + Vp.length * Up + 600;
export function Scene_qp({ value: e, t, start: n, cents: r }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let i = e * $d(t, n, 700);
  return (
    <React.Fragment>
      {translateShowcaseNode(
        showcaseCopy,
        Bp(r ? Math.round(i * 100) / 100 : Math.round(i), r)
      )}
    </React.Fragment>
  );
}
export function CostBreakdown({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = Gp.high * 1.04,
    n = (e) => `${(e / t) * 100}%`,
    r = 3e3,
    i = $d(e, Kp - 400, 900) * (Wp.base / w.reserve);
  return (
    <div className={'mt-4 flex flex-col gap-3'}>
      <div {...J(e, r)} className={'pd-in flex flex-col gap-1.5'}>
        <span className={'text-ink-3 text-[12px]'}>
          {translateShowcaseNode(showcaseCopy, 'Packed unit, factory')}
        </span>
        <span className={'relative block h-2'}>
          <span
            className={'absolute inset-x-0 top-1/2 h-px bg-[var(--border)]'}
          />
          <span
            data-on={String(e >= r)}
            className={
              'pd-grow absolute inset-y-0 rounded-full bg-[var(--state-wash)] shadow-[inset_0_0_0_1px_var(--border)]'
            }
            style={{
              left: n(Gp.low),
              width: `calc(${n(Gp.high)} - ${n(Gp.low)})`,
            }}
          />
          <span
            className={'absolute -inset-y-1 w-[1.5px] bg-[var(--foreground)]'}
            style={{
              left: n(Gp.base),
            }}
          />
        </span>
        <span className={'num text-ink-4 relative h-4 font-mono text-[11px]'}>
          <span
            className={'absolute'}
            style={{
              left: n(Gp.low),
            }}
          >
            {translateShowcaseNode(showcaseCopy, Bp(Math.round(Gp.low)))}
          </span>
          <span
            className={'text-ink absolute -translate-x-1/2'}
            style={{
              left: n(Gp.base),
            }}
          >
            {translateShowcaseNode(showcaseCopy, Bp(Math.round(Gp.base)))}
          </span>
          <span className={'absolute right-0'}>
            {translateShowcaseNode(showcaseCopy, Bp(Math.round(Gp.high)))}
          </span>
        </span>
      </div>
      <div {...J(e, Kp - 400)} className={'pd-in flex flex-col gap-1.5'}>
        <span
          className={'flex items-baseline justify-between gap-3 text-[12px]'}
        >
          <span className={'text-ink-3'}>
            {translateShowcaseNode(
              showcaseCopy,
              'Base envelope against the reserve'
            )}
          </span>
          <span className={'num text-ink-3 font-mono'}>
            {translateShowcaseNode(showcaseCopy, Bp(Wp.base))}
            {translateShowcaseNode(showcaseCopy, ' / ~')}
            {translateShowcaseNode(showcaseCopy, Bp(w.reserve))}
          </span>
        </span>
        <span
          className={
            'block h-1 overflow-hidden rounded-full bg-[var(--tint-2)]'
          }
        >
          <span
            className={'block h-full rounded-full bg-[var(--foreground)]'}
            style={{
              width: `${i * 100}%`,
            }}
          />
        </span>
      </div>
    </div>
  );
}
export function CostRange({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'flex min-w-0 flex-col'}>
      <div
        className={
          'grid grid-cols-[minmax(0,1fr)_repeat(3,auto)] gap-x-3 sm:gap-x-4'
        }
      >
        {translateShowcaseNode(
          showcaseCopy,
          ['', 'Low', 'Base', 'High'].map((e, t) => (
            <span
              key={e || 'label'}
              className={`text-ink-4 border-b border-[var(--line)] pb-1.5 text-[12px] font-medium ${t > 0 ? 'text-right' : ''} ${t === 2 ? 'text-ink' : ''}`}
            >
              {translateShowcaseNode(
                showcaseCopy,
                e || `${w.quantity} units, EUR`
              )}
            </span>
          ))
        )}
        {translateShowcaseNode(
          showcaseCopy,
          Vp.map((t, n) => {
            let r = Hp + n * Up,
              i = n < 2,
              a = t === Wp,
              o = `num border-b border-[var(--line)] py-2 text-right font-mono text-[12.5px] whitespace-nowrap ${a ? 'font-semibold' : ''}`;
            return (
              <React.Fragment key={t.label}>
                <span
                  {...J(e, r)}
                  className={`pd-in border-b border-[var(--line)] py-2 text-[13px] leading-[1.35] ${a ? 'text-ink font-semibold' : 'text-ink-2'}`}
                >
                  {translateShowcaseNode(showcaseCopy, t.label)}
                </span>
                <span {...J(e, r)} className={`pd-in text-ink-3 ${o}`}>
                  <Scene_qp value={t.low} t={e} start={r} cents={i} />
                </span>
                <span
                  {...J(e, r)}
                  className={`pd-in text-ink bg-[var(--tint)] px-1.5 ${o}`}
                >
                  <Scene_qp value={t.base} t={e} start={r} cents={i} />
                </span>
                <span {...J(e, r)} className={`pd-in text-ink-3 ${o}`}>
                  <Scene_qp value={t.high} t={e} start={r} cents={i} />
                </span>
              </React.Fragment>
            );
          })
        )}
      </div>
      <CostBreakdown t={e} />
    </div>
  );
}
export const Xp = {
  Recommended: 'green',
  'Good hybrid': 'neutral',
  'Not for a first run': 'amber',
};
export const Zp = 300;
export const Qp = 340;
export const $p = Zp + w.routes.length * Qp + 150;
export function EstimateBadge({ className: e = '' }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <span
      className={`text-ink-4 inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[11px] font-medium whitespace-nowrap shadow-[inset_0_0_0_1px_var(--line)] ${e}`}
    >
      {translateShowcaseNode(showcaseCopy, 'Planning estimate')}
    </span>
  );
}
export function SourcingOption({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = ef(e, Zp, Qp, w.routes.length);
  return (
    <ul className={'flex flex-col'}>
      {translateShowcaseNode(
        showcaseCopy,
        w.routes.map((e, n) => (
          <li
            key={e.name}
            data-on={String(t > n)}
            className={
              'pd-in flex flex-col gap-0.5 border-b border-[var(--line)] py-2.5 last:border-0'
            }
          >
            <span className={'flex items-center gap-2'}>
              <span className={'text-ink text-[13.5px] font-medium'}>
                {translateShowcaseNode(showcaseCopy, e.name)}
              </span>
              <ProductionBadge
                tone={Xp[e.verdict] ?? 'neutral'}
                className={'ml-auto h-5 px-1.5 text-[11px]'}
              >
                {translateShowcaseNode(
                  showcaseCopy,
                  e.verdict === 'Recommended' ? (
                    <ShowcaseIcon
                      name={'check'}
                      className={'size-3'}
                      strokeWidth={2}
                    />
                  ) : null
                )}
                {translateShowcaseNode(showcaseCopy, e.verdict)}
              </ProductionBadge>
            </span>
            <span
              className={'text-ink-3 text-[12.5px] leading-[1.45] text-pretty'}
            >
              {translateShowcaseNode(showcaseCopy, e.why)}
            </span>
          </li>
        ))
      )}
    </ul>
  );
}
export function SourcingTable({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div {...J(e, $p)} className={'pd-in flex flex-col'}>
      <div
        className={
          'text-ink-4 grid grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] gap-x-2 border-b border-[var(--line)] pb-1.5 text-[12px] font-medium'
        }
      >
        <span>{translateShowcaseNode(showcaseCopy, 'Region')}</span>
        <span>{translateShowcaseNode(showcaseCopy, 'Unit')}</span>
        <span>{translateShowcaseNode(showcaseCopy, 'MOQ')}</span>
        <span>{translateShowcaseNode(showcaseCopy, 'Lead time')}</span>
      </div>
      {translateShowcaseNode(
        showcaseCopy,
        w.regions.map((e, t) => (
          <div
            key={e.name}
            className={`num grid grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] gap-x-2 border-b border-[var(--line)] py-2 text-[12.5px] leading-[1.35] last:border-0 ${t === 0 ? 'text-ink' : 'text-ink-2'}`}
          >
            <span className={'font-medium'}>
              {translateShowcaseNode(showcaseCopy, e.name)}
            </span>
            <span className={'font-mono'}>
              {translateShowcaseNode(showcaseCopy, e.unit)}
            </span>
            <span>{translateShowcaseNode(showcaseCopy, e.moq)}</span>
            <span>{translateShowcaseNode(showcaseCopy, e.lead)}</span>
          </div>
        ))
      )}
    </div>
  );
}
